import { useState } from 'react';
import { FileSearch } from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@/shared/ui/shadcn/command';

import { usePatientGlobalSearch } from '../model/usePatientGlobalSearch';
import type { PatientGlobalSearchHit } from '../model/types';

interface PatientGlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | undefined;
  onSelectHit: (hit: PatientGlobalSearchHit) => void;
  resultLimit?: number;
}

export function PatientGlobalSearchDialog({
  open,
  onOpenChange,
  userId,
  onSelectHit,
  resultLimit,
}: PatientGlobalSearchDialogProps) {
  const [query, setQuery] = useState('');
  const { groupedResults, isLoading, isError, results } = usePatientGlobalSearch({
    userId,
    query,
    limit: resultLimit,
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen === false) {
      setQuery('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Global patient search"
      description="Search across medications, labs, vitals, conditions, notes, and prescriptions."
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search all chart resources…"
        aria-label="Global patient search"
      />
      <CommandList>
        {isLoading ? <CommandEmpty>Loading chart data…</CommandEmpty> : null}
        {isError ? <CommandEmpty>Could not load search data.</CommandEmpty> : null}
        {!isLoading && !isError && results.length === 0 ? (
          <CommandEmpty>No matching records found.</CommandEmpty>
        ) : null}
        {!isLoading && !isError
          ? groupedResults.map((group) => (
              <CommandGroup key={group.resource} heading={group.label}>
                {group.hits.map((hit) => {
                  const value = `${hit.title} ${hit.subtitle} ${hit.keywords}`;
                  return (
                    <CommandItem
                      key={hit.id}
                      value={value}
                      onSelect={() => {
                        onSelectHit(hit);
                        handleOpenChange(false);
                      }}
                    >
                      <FileSearch className="h-4 w-4" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{hit.title}</p>
                        <p className="truncate text-xs text-muted-foreground">{hit.subtitle}</p>
                      </div>
                      <CommandShortcut>{hit.resource.replace(/^[^:]+:/, '')}</CommandShortcut>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))
          : null}
      </CommandList>
    </CommandDialog>
  );
}
